import React, { useMemo, useState } from 'react';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { type FilterPreset } from '@/components/shared/filters';
import { useSnackbar } from 'notistack';
import TradingAccountDetailsModal from './Customers/components/TradingAccountDetailsModal';
import { useCustomers } from '../../api/customers/use-customers';
import { formatCurrencyAmount, formatNumber } from '@/utils/formatters.ts';
import { useExportTradingAccounts } from '../../api/export/use-export';
import {
    TradingAccount,
    useTradingAccounts,
} from '@/api/trading-accounts/use-crm-trading-accounts.ts';
import { useUrlState } from '@/hooks/shared';
import { AlertTriangle, DollarSign, TrendingDown, TrendingUp, Wallet, XCircle } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';

const TradingAccountsPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'created_at', direction: 'desc' },
        pagination: { page: 1, pageSize: 25 },
    });

    // State for details modal
    const [selectedAccount, setSelectedAccount] = useState<TradingAccount | null>(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    // API hooks - only pass filters that have values, like LeadsPage
    const { data: customers } = useCustomers();
    const exportTradingAccountsMutation = useExportTradingAccounts(
        () => {
            enqueueSnackbar('Trading accounts exported successfully', { variant: 'success' });
        },
        (error: Error) => {
            console.error('Export failed:', error);
            enqueueSnackbar('Failed to export trading accounts', { variant: 'error' });
        }
    );

    const handleExport = () => {
        exportTradingAccountsMutation.mutate({
            format: 'csv',
            filters: { ...filters },
        });
    };

    const {
        data: tradingAccountsData,
        isLoading,
        refetch,
    } = useTradingAccounts({
        page: pagination.page,
        per_page: pagination.pageSize,
        sort_by: sort.key,
        sort_direction: sort.direction,
        ...Object.fromEntries(
            Object.entries(filters).filter(
                ([key, value]) =>
                    value !== undefined && value !== '' && value !== null && value !== 'all'
            )
        ),
    });

    // Extract data
    const accounts = tradingAccountsData?.data || [];
    const totalAccounts = tradingAccountsData?.meta?.total || 0;
    const totalPages = tradingAccountsData?.meta?.last_page || 1;

    // Calculate statistics
    const accountStats = {
        totalAccounts,
        totalBalance: accounts.reduce(
            (sum: number, account: TradingAccount) => sum + Number(account.balance),
            0
        ),
        totalEquity: accounts.reduce(
            (sum: number, account: TradingAccount) => sum + Number(account.equity),
            0
        ),
        totalUsedMargin: accounts.reduce(
            (sum: number, account: TradingAccount) => sum + Number(account.used_margin),
            0
        ),
        marginCallAccounts: accounts.filter(
            (account: TradingAccount) => account.margin_status.margin_call
        ).length,
        liquidationRiskAccounts: accounts.filter(
            (account: TradingAccount) => account.margin_status.liquidation_warning
        ).length,
    };

    // Handle filter changes
    const handleFilterChange = (newFilters: Record<string, any>) => {
        const cleanedFilters = Object.fromEntries(
            Object.entries(newFilters).map(([key, value]) => [
                key,
                value === 'all' ? undefined : value,
            ])
        );
        updateFilters(cleanedFilters);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    const handleSort = (key: keyof TradingAccount) => {
        toggleSort(key as string);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    // Define filter fields for the GenericDataTable
    const filterFields = [
        {
            key: 'search',
            label: 'Search',
            type: 'text' as const,
            placeholder: 'Search accounts...',
        },
        {
            key: 'customer_id',
            label: 'Customer',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Customers' },
                ...(customers?.map(customer => ({
                    value: customer.id,
                    label: customer.name,
                })) || []),
            ],
        },
        {
            key: 'base_currency',
            label: 'Currency',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Currencies' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'GBP', label: 'GBP' },
            ],
        },
        {
            key: 'margin_status',
            label: 'Margin Status',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Statuses' },
                { value: 'normal', label: 'Normal' },
                { value: 'margin_call', label: 'Margin Call' },
                { value: 'liquidation_warning', label: 'Liquidation Warning' },
            ],
        },
    ];

    // Save filter preset
    const handleSavePreset = (preset: Omit<FilterPreset, 'id'>) => {
        enqueueSnackbar(`Filter preset "${preset.name}" saved`, { variant: 'success' });
    };

    // Filter presets
    const filterPresets: FilterPreset[] = useMemo(
        () => [
            {
                id: 'usd-accounts',
                name: 'USD Accounts',
                description: 'Trading accounts with USD base currency',
                isDefault: true,
                filters: {
                    base_currency: 'USD',
                },
            },
            {
                id: 'eur-accounts',
                name: 'EUR Accounts',
                description: 'Trading accounts with EUR base currency',
                isDefault: true,
                filters: {
                    base_currency: 'EUR',
                },
            },
            {
                id: 'margin-call-accounts',
                name: 'Margin Call',
                description: 'Accounts in margin call status',
                isDefault: true,
                filters: {
                    margin_status: 'margin_call',
                },
            },
            {
                id: 'liquidation-warning',
                name: 'Liquidation Warning',
                description: 'Accounts with liquidation warning',
                isDefault: true,
                filters: {
                    margin_status: 'liquidation_warning',
                },
            },
            {
                id: 'normal-accounts',
                name: 'Normal Status',
                description: 'Accounts with normal margin status',
                isDefault: true,
                filters: {
                    margin_status: 'normal',
                },
            },
            {
                id: 'high-balance-accounts',
                name: 'High Balance',
                description: 'Accounts with balance above $10,000',
                isDefault: true,
                filters: {
                    min_balance: 10000,
                },
            },
        ],
        []
    );

    // Define columns for the DataTable
    const columns: Column<TradingAccount>[] = [
        {
            id: 'account_number',
            label: 'Account #',
            key: 'account_number',
            sortable: true,
            width: '120px',
            render: (_, account) => (
                <div className="font-mono font-medium text-success">
                    #{account.account_number || 'N/A'}
                </div>
            ),
        },
        {
            id: 'customer',
            label: 'Customer',
            key: 'customer',
            sortable: true,
            width: '200px',
            render: (_, account) => (
                <div>
                    <div className="font-medium">{account.customer?.name || 'N/A'}</div>
                    <div className="text-sm text-muted-foreground">
                        {account.customer?.email || 'N/A'}
                    </div>
                </div>
            ),
        },
        {
            id: 'currency',
            label: 'Currency',
            key: 'base_currency',
            sortable: true,
            width: '150px',
            render: (_, account) => (
                <div>
                    <div className="font-medium">{account.base_currency}</div>
                    <div className="text-sm text-muted-foreground">{account.leverage}x</div>
                </div>
            ),
        },
        {
            id: 'balance',
            label: 'Balance',
            key: 'balance',
            sortable: true,
            width: '120px',
            render: (_, account) => (
                <div className="font-medium">
                    {formatCurrencyAmount(account.balance, account.base_currency)}
                </div>
            ),
        },
        {
            id: 'equity',
            label: 'Equity',
            key: 'equity',
            sortable: true,
            width: '120px',
            render: (_, account) => (
                <div className="font-medium">
                    {formatCurrencyAmount(account.equity, account.base_currency)}
                </div>
            ),
        },
        {
            id: 'used_margin',
            label: 'Used Margin',
            key: 'used_margin',
            sortable: true,
            width: '130px',
            render: (_, account) => (
                <div className="font-medium">
                    {formatCurrencyAmount(account.used_margin, account.base_currency)}
                </div>
            ),
        },
        {
            id: 'margin_level',
            label: 'Margin Level',
            key: 'margin_level',
            sortable: true,
            width: '120px',
            render: (_, account) => (
                <div className="font-medium">{formatNumber(account.margin_level)}%</div>
            ),
        },
        {
            id: 'daily_pnl',
            label: 'Daily PnL',
            key: 'daily_pnl',
            sortable: true,
            width: '120px',
            render: (_, account) => (
                <div
                    className={`font-medium flex items-center gap-1 ${getPnLColor(account.daily_pnl)}`}
                >
                    {account.daily_pnl > 0 ? (
                        <TrendingUp className="h-4 w-4" />
                    ) : account.daily_pnl < 0 ? (
                        <TrendingDown className="h-4 w-4" />
                    ) : null}
                    {formatCurrencyAmount(account.daily_pnl, account.base_currency)}
                </div>
            ),
        },
        {
            id: 'status',
            label: 'Status',
            key: 'margin_status',
            sortable: true,
            width: '120px',
            render: (_, account) => getMarginStatusBadge(account),
        },
    ];

    // Handle edit account
    const handleViewDetails = (account: TradingAccount) => {
        setSelectedAccount(account);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
    };

    // Get margin status badge
    const getMarginStatusBadge = (account: TradingAccount) => {
        if (account.margin_status.liquidation_warning) {
            return <StatusBadge status="suspended" kind="account" label="Liquidation Risk" />;
        }
        if (account.margin_status.margin_call) {
            return <StatusBadge status="inactive" kind="account" label="Margin Call" />;
        }
        if (account.margin_status.can_trade) {
            return <StatusBadge status="active" kind="account" />;
        }
        return <StatusBadge status="inactive" kind="account" label="Inactive" />;
    };

    // Get PnL color
    const getPnLColor = (pnl: number) => {
        if (pnl > 0) return 'text-success';
        if (pnl < 0) return 'text-destructive';
        return 'text-muted-foreground';
    };

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Trading Accounts"
                description="Manage and monitor all customer trading accounts"
            />

            <StatTileGrid className="shrink-0">
                <StatTile
                    label="Total Accounts"
                    value={formatNumber(accountStats.totalAccounts)}
                    hint="Active trading accounts"
                    icon={Wallet}
                    tone="brand"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Total Balance"
                    value={formatCurrencyAmount(accountStats.totalBalance, 'USD')}
                    hint="Combined account balances"
                    icon={DollarSign}
                    tone="brand"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Margin Call"
                    value={accountStats.marginCallAccounts}
                    hint="Accounts in margin call"
                    icon={AlertTriangle}
                    tone="warning"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Liquidation Risk"
                    value={accountStats.liquidationRiskAccounts}
                    hint="High risk accounts"
                    icon={XCircle}
                    tone="destructive"
                    isLoading={isLoading}
                />
            </StatTileGrid>

            <GenericDataTable<TradingAccount>
                data={accounts}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={filterPresets}
                isLoading={isLoading}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={account => account.id}
                sortConfig={{
                    key: sort.key as keyof TradingAccount,
                    direction: sort.direction,
                }}
                onSort={handleSort}
                totalItems={totalAccounts}
                totalPages={totalPages}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onExport={handleExport}
                showExportButton={hasPermission(
                    ModulePermission.TRADING_ACCOUNTS,
                    PermissionAction.EXPORT
                )}
                onRowClick={handleViewDetails}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onLoadPreset={preset => handleFilterChange(preset.filters)}
                onSavePreset={handleSavePreset}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No trading accounts found."
                loadingMessage="Loading accounts…"
                showRowClick={true}
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={Wallet}
                        title="No trading accounts found"
                        description={
                            hasActiveFilters
                                ? 'No account matches the current filters. Try clearing them.'
                                : 'Trading accounts will appear here once customers open one.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />

            {/* Trading Account Details Modal */}
            <TradingAccountDetailsModal
                open={isDetailsModalOpen}
                onClose={handleCloseDetailsModal}
                account={selectedAccount}
            />
        </PageContainer>
    );
};

export default TradingAccountsPage;
